import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PageHeader from './Navigation/PageHeader';
import { motion } from 'framer-motion';
import * as caseService from '../services/caseService';
import * as staffService from '../services/staffService';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import useModalAccessibility from '../hooks/useModalAccessibility';
import { checkCaseDependencies } from '../services/dependencyService';
import { useAuth } from './Auth/AuthProvider';
import CustomDatePicker from './common/CustomDatePicker';

const {
  FiFolder,
  FiUser,
  FiCalendar,
  FiFileText,
  FiClock,
  FiCheck,
  FiAlertTriangle,
  FiEye,
  FiDownload,
  FiEdit3,
  FiTarget,
  FiTrendingUp,
  FiUsers,
  FiShield,
  FiSettings,
  FiScale,
  FiUserX,
  FiPlus,
  FiTrash2,
  FiGrid,
  FiList,
  FiChevronLeft,
  FiChevronRight,
  FiPlay,
  FiCheckCircle
} = FiIcons;

const CaseManagement = () => {
  const location = useLocation();
  const { isAdmin } = useAuth();
  const [cases, setCases] = useState([]);
  const [staff, setStaff] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedWorkflow, setSelectedWorkflow] = useState('');
  const [showNewCaseForm, setShowNewCaseForm] = useState(false);
  const [showEditCaseForm, setShowEditCaseForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [caseDependencies, setCaseDependencies] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState(null);

  const workflowTypes = [
    { id: 'disciplinary', name: 'Disciplinary Process', icon: FiIcons.FiAlertTriangle, color: 'red' },
    { id: 'grievance', name: 'Grievance Handling', icon: FiIcons.FiUsers, color: 'orange' },
    { id: 'performance-capability', name: 'Performance/Capability Management', icon: FiIcons.FiTrendingUp, color: 'yellow' },
    { id: 'absence-management', name: 'Long-term Absence Management', icon: FiIcons.FiCalendar, color: 'blue' },
    { id: 'exit-dismissal', name: 'Exit/Dismissal Process', icon: FiIcons.FiUserX, color: 'purple' },
    { id: 'tribunal-prep', name: 'Tribunal Preparation', icon: FiIcons.FiScale, color: 'gray' },
    { id: 'settlement', name: 'Settlement Agreement', icon: FiIcons.FiFileText, color: 'green' }
  ];

  useEffect(() => {
    loadData();

    if (location.state?.filterEmployee) {
      setSelectedEmployee(location.state.filterEmployee);
    }

    if (location.state?.action === 'create') {
      setShowNewCaseForm(true);
    }
  }, [location.state]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load both cases and staff in parallel
      // Non-admin users only see cases they created
      const [casesData, staffData] = await Promise.all([
        caseService.getAllCases({ onlyMine: !isAdmin }),
        staffService.getAllStaff()
      ]);

      // Calculate progress for each case
      const casesWithProgress = casesData.map(caseItem => {
        const progress = calculateProgress(caseItem.case_stages || []);
        return {
          ...caseItem,
          progress
        };
      });

      setCases(casesWithProgress);
      setStaff(staffData);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load cases. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const calculateProgress = (stages) => {
    let totalActions = 0;
    let completedActions = 0;

    stages.forEach(stage => {
      const actions = stage.case_actions || [];
      totalActions += actions.length;
      completedActions += actions.filter(action => action.is_completed === true).length;
    });

    return {
      completedActions,
      totalActions,
      completionPercentage: totalActions > 0 ? Math.round((completedActions / totalActions) * 100) : 0
    };
  };

  const createNewCase = async (formData) => {
    try {
      setLoading(true);
      setError(null);

      const employee = staff.find(emp => emp.id === formData.employeeId);
      const workflow = workflowTypes.find(wf => wf.id === formData.workflowType);

      const caseData = {
        staff_id: formData.employeeId,
        case_type: formData.workflowType,
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        status: 'active',
        actual_created_date: formData.actualCreatedDate ? new Date(formData.actualCreatedDate).toISOString() : null  // NEW: Support backdating
      };

      await caseService.createCase(caseData);

      // Reload cases after creation
      await loadData();
      setShowNewCaseForm(false);
    } catch (err) {
      console.error('Error creating case:', err);
      setError('Failed to create case. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditCase = (caseItem) => {
    setSelectedCase(caseItem);
    setShowEditCaseForm(true);
  };

  const updateCase = async (formData) => {
    try {
      setLoading(true);
      setError(null);

      await caseService.updateCase(selectedCase.id, {
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        status: formData.status
      });

      // Reload cases after update
      await loadData();
      setShowEditCaseForm(false);
      setSelectedCase(null);
    } catch (err) {
      console.error('Error updating case:', err);
      setError('Failed to update case. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCase = async (caseItem) => {
    setSelectedCase(caseItem);
    const dependencies = await checkCaseDependencies(caseItem.id);
    setCaseDependencies(dependencies);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      setDeleteLoading(true);
      setError(null);

      await caseService.deleteCase(selectedCase.id);

      // Reload cases after deletion
      await loadData();
      setShowDeleteConfirm(false);
      setSelectedCase(null);
      setCaseDependencies({});
    } catch (err) {
      console.error('Error deleting case:', err);
      setError('Failed to delete case. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const initializeWorkflowStages = (workflowType) => {
    const stageTemplates = {
      disciplinary: [
        {
          name: 'Early Intervention',
          timeline: '1-2 weeks',
          actions: [
            { name: 'Informal discussion with employee about concerns', legal: false, acas: true },
            { name: 'Document concerns and discussion outcomes', legal: true, acas: false },
            { name: 'Set clear expectations and improvement targets', legal: false, acas: true },
            { name: 'Offer support, training, or reasonable adjustments', legal: true, acas: false }
          ]
        },
        {
          name: 'Formal Investigation',
          timeline: '2-4 weeks',
          actions: [
            { name: 'Suspend employee if necessary (with pay, not disciplinary action)', legal: true, acas: false },
            { name: 'Appoint investigating officer (independent from line management)', legal: true, acas: false },
            { name: 'Gather evidence: witness statements, documents, CCTV', legal: true, acas: false },
            { name: 'Interview relevant parties and witnesses', legal: true, acas: false },
            { name: 'Prepare investigation report with findings', legal: true, acas: false }
          ]
        },
        {
          name: 'Disciplinary Hearing',
          timeline: '1-2 weeks',
          actions: [
            { name: 'Send disciplinary invitation letter (minimum 48 hours notice)', legal: true, acas: false },
            { name: 'Include: allegations, evidence, right to be accompanied', legal: true, acas: false },
            { name: 'Hold hearing with independent chair', legal: true, acas: false },
            { name: 'Allow employee to present case and call witnesses', legal: true, acas: false },
            { name: 'Adjourn to consider decision and communicate outcome in writing', legal: true, acas: false }
          ]
        },
        {
          name: 'Outcomes & Sanctions',
          timeline: '1 week',
          actions: [
            { name: 'Determine appropriate sanction (warning, final warning, dismissal)', legal: true, acas: false },
            { name: 'Issue written outcome letter with reasons', legal: true, acas: false },
            { name: 'Explain right of appeal (5 working days to lodge)', legal: true, acas: false },
            { name: 'Set up monitoring arrangements if warnings issued', legal: false, acas: true }
          ]
        }
      ],
      grievance: [
        {
          name: 'Informal Resolution',
          timeline: '1-2 weeks',
          actions: [
            { name: 'Employee raises concern with line manager', legal: false, acas: true },
            { name: 'Attempt informal resolution through discussion', legal: false, acas: true },
            { name: 'Document attempted resolution', legal: true, acas: false },
            { name: 'If unresolved, advise of formal grievance process', legal: false, acas: true }
          ]
        },
        {
          name: 'Formal Grievance',
          timeline: '2-4 weeks',
          actions: [
            { name: 'Employee submits written grievance', legal: true, acas: false },
            { name: 'Acknowledge receipt within 5 working days', legal: true, acas: false },
            { name: 'Appoint investigating manager (independent)', legal: true, acas: false },
            { name: 'Invite to grievance hearing (48 hours notice)', legal: true, acas: false },
            { name: 'Confirm right to be accompanied', legal: true, acas: false }
          ]
        },
        {
          name: 'Investigation & Hearing',
          timeline: '3-5 weeks',
          actions: [
            { name: 'Investigate grievance thoroughly', legal: true, acas: false },
            { name: 'Interview relevant parties and witnesses', legal: true, acas: false },
            { name: 'Hold formal grievance hearing', legal: true, acas: false },
            { name: 'Allow employee to present case', legal: true, acas: false },
            { name: 'Adjourn to consider decision', legal: false, acas: true }
          ]
        },
        {
          name: 'Outcome & Appeal',
          timeline: '1-2 weeks',
          actions: [
            { name: 'Communicate decision in writing', legal: true, acas: false },
            { name: 'Explain reasons for decision', legal: true, acas: false },
            { name: 'Offer right of appeal', legal: true, acas: false },
            { name: 'Implement any agreed changes', legal: false, acas: true },
            { name: 'Monitor ongoing situation', legal: false, acas: true }
          ]
        }
      ]
      // Add other workflow types as needed...
    };

    return stageTemplates[workflowType] || [];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'on-hold': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const filteredCases = cases.filter(caseItem => {
    const matchesStatus = filterStatus === 'all' || caseItem.status === filterStatus;
    const matchesSearch =
      caseItem.staff?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      caseItem.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      caseItem.case_id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesEmployee = selectedEmployee === '' || caseItem.staff_id === selectedEmployee;

    return matchesStatus && matchesSearch && matchesEmployee;
  });

  // Pagination
  const totalPages = Math.ceil(filteredCases.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCases = filteredCases.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, searchTerm, selectedEmployee]);

  // Stats for page header
  const headerStats = [
    {
      label: 'Total Cases',
      value: cases.length,
      icon: FiFolder,
      iconColor: 'text-blue-600'
    },
    {
      label: 'Active Cases',
      value: cases.filter(c => c.status === 'active').length,
      icon: FiClock,
      iconColor: 'text-blue-600'
    },
    {
      label: 'Tribunal Risk',
      value: cases.filter(c => c.case_type === 'tribunal-prep').length,
      icon: FiScale,
      iconColor: 'text-red-600'
    },
    {
      label: 'High Priority',
      value: cases.filter(c => c.priority === 'high').length,
      icon: FiAlertTriangle,
      iconColor: 'text-red-600'
    }
  ];

  const headerActions = [
    {
      type: 'link',
      label: 'Staff Directory',
      path: '/staff',
      icon: FiUser,
      variant: 'secondary'
    },
    {
      type: 'button',
      label: 'New Case',
      icon: FiPlus,
      variant: 'primary',
      onClick: () => setShowNewCaseForm(true)
    }
  ];

  if (loading) {
    return (
      <>
        <PageHeader
          title="Employee Relations Case Management"
          subtitle="Focused case management for employee relations and exit/tribunal risk processes"
          stats={headerStats}
          actions={headerActions}
        />
        <div className="max-w-7xl mx-auto p-6">
          <div className="text-center py-12">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-600 font-medium">Loading cases...</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader
          title="Employee Relations Case Management"
          subtitle="Focused case management for employee relations and exit/tribunal risk processes"
          stats={headerStats}
          actions={headerActions}
        />
        <div className="max-w-7xl mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <SafeIcon icon={FiAlertTriangle} className="text-red-600 text-4xl mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-red-900 mb-2">Error Loading Data</h3>
            <p className="text-red-700 mb-4">{error}</p>
            <button
              onClick={loadData}
              className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Employee Relations Case Management"
        subtitle="Focused case management for employee relations and exit/tribunal risk processes"
        stats={headerStats}
        actions={headerActions}
      />

      <div className="max-w-7xl mx-auto p-6">
        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-64">
              <input
                type="text"
                placeholder="Search cases, employees, or case IDs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Employees</option>
              {staff.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.full_name} ({emp.department})
                </option>
              ))}
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="on-hold">On Hold</option>
              <option value="cancelled">Cancelled</option>
            </select>

            {selectedEmployee && (
              <button
                onClick={() => setSelectedEmployee('')}
                className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
              >
                Clear Employee Filter
              </button>
            )}

            {/* View Mode Toggle */}
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-4 py-2 flex items-center space-x-2 transition-colors ${viewMode === 'grid'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                title="Grid View"
                aria-label="Switch to Grid View"
              >
                <SafeIcon icon={FiGrid} />
                <span className="hidden sm:inline">Grid</span>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`hidden md:flex px-4 py-2 items-center space-x-2 transition-colors ${viewMode === 'list'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                title="List View"
                aria-label="Switch to List View"
              >
                <SafeIcon icon={FiList} />
                <span className="hidden sm:inline">List</span>
              </button>
            </div>
          </div>
        </div>

        {/* Cases Grid View */}
        {viewMode === 'grid' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {paginatedCases.map((caseItem, index) => (
              <motion.div
                key={caseItem.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-bold text-lg text-gray-900 mb-1">{caseItem.title}</h3>
                      <p className="text-sm text-gray-600">{caseItem.case_id}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(caseItem.status)}`}>
                      {caseItem.status.replace('-', ' ').toUpperCase()}
                    </span>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiUser} className="text-gray-500" />
                      <span className="text-sm font-medium">{caseItem.staff?.full_name || 'Unknown'}</span>
                      <span className="text-xs text-gray-500">({caseItem.staff?.department || 'N/A'})</span>
                    </div>

                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiFolder} className="text-gray-500" />
                      <span className="text-sm">{workflowTypes.find(w => w.id === caseItem.case_type)?.name || caseItem.case_type}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <SafeIcon icon={FiCalendar} className="text-gray-500" />
                        <span className="text-xs text-gray-500">
                          Created: {new Date(caseItem.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <span className={`text-xs font-medium ${getPriorityColor(caseItem.priority)}`}>
                        {caseItem.priority?.toUpperCase()} PRIORITY
                      </span>
                    </div>
                  </div>

                  {/* Current Stage */}
                  {(() => {
                    const stages = caseItem.case_stages || [];
                    const activeStage = stages.find(s => s.status === 'active');
                    const completedStages = stages.filter(s => s.status === 'completed');
                    const lastCompletedStage = completedStages.length > 0
                      ? completedStages.sort((a, b) => (b.stage_order || 0) - (a.stage_order || 0))[0]
                      : null;
                    const currentStage = activeStage || lastCompletedStage;
                    const isCompleted = !activeStage && lastCompletedStage;

                    return currentStage ? (
                      <div className="mb-3">
                        <span className="text-xs text-gray-500 block mb-1">
                          {isCompleted ? 'Completed Stage' : 'Current Stage'}
                        </span>
                        <div className="flex items-center space-x-2">
                          <SafeIcon
                            icon={isCompleted ? FiCheckCircle : FiPlay}
                            className={isCompleted ? 'text-green-500' : 'text-blue-500'}
                          />
                          <span className="text-sm font-medium text-gray-700">
                            {currentStage.stage_name}
                          </span>
                        </div>
                      </div>
                    ) : null;
                  })()}

                  {/* Enhanced Progress Bar with Action Tracking */}
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-medium text-gray-700">Action Progress</span>
                      <span className="text-xs text-gray-500">
                        {caseItem.progress?.completedActions || 0}/{caseItem.progress?.totalActions || 0} actions
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${caseItem.progress?.completionPercentage || 0}%` }}
                      />
                    </div>
                    <div className="text-xs text-gray-500 text-right">
                      {caseItem.progress?.completionPercentage || 0}% complete
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex space-x-2">
                    <a
                      href={`#/case/${caseItem.id}`}
                      className="flex-1 bg-blue-600 text-white text-center py-2 rounded hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      View Details
                    </a>
                    <button
                      onClick={() => handleEditCase(caseItem)}
                      className="px-3 py-2 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                      title="Edit Case"
                      aria-label={`Edit case ${caseItem.case_id}`}
                    >
                      <SafeIcon icon={FiEdit3} className="text-gray-600" />
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => handleDeleteCase(caseItem)}
                        className="px-3 py-2 border border-red-300 rounded hover:bg-red-50 transition-colors"
                        title="Delete Case"
                        aria-label={`Delete case ${caseItem.case_id}`}
                      >
                        <SafeIcon icon={FiTrash2} className="text-red-600" />
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Cases List View */}
        {viewMode === 'list' && (
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Case</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedCases.map((caseItem) => (
                  <motion.tr
                    key={caseItem.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <button
                          onClick={() => window.location.hash = `/case/${caseItem.id}`}
                          className="text-sm font-medium text-blue-600 hover:text-blue-900 hover:underline text-left"
                        >
                          {caseItem.title}
                        </button>
                        <div className="text-sm text-gray-500">{caseItem.case_id}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{caseItem.staff?.full_name || 'Unknown'}</div>
                      <div className="text-sm text-gray-500">{caseItem.staff?.department || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {workflowTypes.find(w => w.id === caseItem.case_type)?.name || caseItem.case_type}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(caseItem.status)}`}>
                        {caseItem.status.replace('-', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-full bg-gray-200 rounded-full h-2 mr-2" style={{ width: '100px' }}>
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${caseItem.progress?.completionPercentage || 0}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-900">{caseItem.progress?.completionPercentage || 0}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-xs font-medium ${getPriorityColor(caseItem.priority)}`}>
                        {caseItem.priority?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(caseItem.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-3">
                        <button
                          onClick={() => window.location.hash = `/case/${caseItem.id}`}
                          className="text-blue-600 hover:text-blue-900 p-1"
                          title="View Details"
                          aria-label={`View details for case ${caseItem.case_id}`}
                        >
                          <SafeIcon icon={FiEye} className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleEditCase(caseItem)}
                          className="text-gray-600 hover:text-gray-900 p-1"
                          title="Edit Case"
                          aria-label={`Edit case ${caseItem.case_id}`}
                        >
                          <SafeIcon icon={FiEdit3} className="w-5 h-5" />
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => handleDeleteCase(caseItem)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Delete Case"
                            aria-label={`Delete case ${caseItem.case_id}`}
                          >
                            <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {filteredCases.length > 0 && totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <div className="text-sm text-gray-700">
              Showing {startIndex + 1} to {Math.min(endIndex, filteredCases.length)} of {filteredCases.length} cases
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
              >
                <SafeIcon icon={FiChevronLeft} />
                <span>Previous</span>
              </button>

              <div className="flex space-x-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-2 rounded-lg ${currentPage === page
                      ? 'bg-blue-600 text-white'
                      : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
              >
                <span>Next</span>
                <SafeIcon icon={FiChevronRight} />
              </button>
            </div>
          </div>
        )}

        {filteredCases.length === 0 && (
          <div className="text-center py-12">
            <SafeIcon icon={FiFolder} className="text-6xl text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No cases found</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm || selectedEmployee
                ? 'No cases match your search criteria.'
                : 'Create your first employee relations case to get started.'}
            </p>
            <button
              onClick={() => setShowNewCaseForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Create New Case
            </button>
          </div>
        )}

        {/* New Case Form Modal */}
        {showNewCaseForm && (
          <NewCaseModal
            employees={staff}
            workflowTypes={workflowTypes}
            onSubmit={createNewCase}
            onCancel={() => setShowNewCaseForm(false)}
          />
        )}

        {/* Edit Case Form Modal */}
        {showEditCaseForm && selectedCase && (
          <EditCaseModal
            caseData={selectedCase}
            onSubmit={updateCase}
            onCancel={() => {
              setShowEditCaseForm(false);
              setSelectedCase(null);
            }}
          />
        )}

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={showDeleteConfirm && selectedCase !== null}
          onClose={() => {
            setShowDeleteConfirm(false);
            setSelectedCase(null);
            setCaseDependencies({});
          }}
          onConfirm={confirmDelete}
          itemType="Case"
          itemName={selectedCase?.case_id || ''}
          dependencies={caseDependencies}
          loading={deleteLoading}
        />
      </div>
    </>
  );
};

// New Case Form Modal Component
const NewCaseModal = ({ employees, workflowTypes, onSubmit, onCancel }) => {
  const modalRef = useModalAccessibility(true, onCancel);
  const [formData, setFormData] = useState({
    employeeId: '',
    workflowType: '',
    title: '',
    description: '',
    priority: 'medium',
    actualCreatedDate: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.employeeId) {
      setError('Please select an employee');
      return;
    }
    if (!formData.workflowType) {
      setError('Please select a case type');
      return;
    }
    if (!formData.title.trim()) {
      setError('Please enter a case title');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(formData);
      // Parent handles closing on success
    } catch (err) {
      console.error('Error creating case:', err);
      setError('Failed to create case. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
      >
        <h2 className="text-xl font-bold text-gray-900 mb-4">Create New Employee Relations Case</h2>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Employee <span className="text-red-500">*</span></label>
            <select
              value={formData.employeeId}
              onChange={(e) => setFormData(prev => ({ ...prev, employeeId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Employee</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.full_name} ({emp.department})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Case Type <span className="text-red-500">*</span></label>
            <select
              value={formData.workflowType}
              onChange={(e) => setFormData(prev => ({ ...prev, workflowType: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Case Type</option>
              {workflowTypes.map(workflow => (
                <option key={workflow.id} value={workflow.id}>
                  {workflow.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Case Title <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Brief description of the case"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              rows="3"
              placeholder="Detailed description of the case"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Case Creation Date (Optional)
            </label>
            <CustomDatePicker
              selected={formData.actualCreatedDate ? new Date(formData.actualCreatedDate) : null}
              onChange={(date) => setFormData(prev => ({ ...prev, actualCreatedDate: date ? date.toISOString() : '' }))}
              placeholder="Select date and time"
              showTimeSelect
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave blank to use current date/time. Set a past date when logging historical cases.
            </p>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex justify-center items-center"
            >
              {submitting ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                'Create Case'
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Edit Case Modal Component
const EditCaseModal = ({ caseData, onSubmit, onCancel }) => {
  const modalRef = useModalAccessibility(true, onCancel);
  const [formData, setFormData] = useState({
    title: caseData.title || '',
    description: caseData.description || '',
    priority: caseData.priority || 'medium',
    status: caseData.status || 'active'
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.title.trim()) {
      setError('Please enter a case title');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(formData);
      // Parent handles closing
    } catch (err) {
      console.error('Error updating case:', err);
      setError('Failed to update case. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
      >
        <h2 className="text-xl font-bold text-gray-900 mb-4">Edit Case</h2>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Case Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priority
            </label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="active">Active</option>
              <option value="on-hold">On Hold</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex justify-center items-center"
            >
              {submitting ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                'Update Case'
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default CaseManagement;